import axios from 'axios';

/** Backend reasons a pay number is blocked because a linked entity is disabled */
export const PAY_NUMBER_INACTIVE_REASONS = [
  'resident_inactive',
  'unit_inactive',
  'building_inactive',
  'bank_account_inactive',
  'company_inactive',
] as const;

/** Returns the inactive-entity reason contained in an error message, if any */
export function getPayNumberInactiveReason(error: unknown): string | undefined {
  const message = error instanceof Error ? error.message : '';
  return PAY_NUMBER_INACTIVE_REASONS.find((reason) => message.includes(reason));
}

/** Handles axios API errors */
export const handleApiError = (error: any) => {
  console.error('Error in API: ', error);

  if (axios.isAxiosError(error) && error?.message === 'Network Error') {
    throw new Error('Network error. Please check your connection.');
  }

  switch (error?.response?.status) {
    case 400:
      throw new Error('Invalid request');
    case 401:
      throw new Error('Unauthorized. Please log in again.');
    case 403:
      throw new Error('Access denied. You do not have permission.');
    case 404:
      throw new Error('Requested resource not found.');
    case 429:
      throw new Error('Too many requests. Please try again later.');
    case 502:
      throw new Error('Bad gateway. Temporary server issue.');
    case 503:
      throw new Error('Service unavailable. Please try again later.');
    case 504:
      throw new Error('Gateway timeout. The server took too long to respond.');
    case 500:
    default:
      throw new Error('Something went wrong. Please try again later.');
  }
};

/**
 * Maps backend API error codes/messages to user-friendly, non-technical strings
 * with explicit action items for the user.
 */
export function formatApiErrorMessage(
  backendMessage: string | undefined,
  defaultFallback: string = 'An error occurred. Please try again.'
): string {
  if (!backendMessage) return defaultFallback;

  const msg = backendMessage.trim();

  // Explicit mappings for backend error keys
  switch (msg) {
    case 'cannot_deactivate_building_with_active_mit_subscription':
    case 'cannot_delete_building_with_active_mit_subscription':
      return 'This building cannot be deactivated because Autopay is active. Please disable Autopay for this building first.';

    case 'cannot_deactivate_building_with_active_equifax_subscription':
    case 'cannot_delete_building_with_active_equifax_subscription':
      return 'This building cannot be deactivated because Credit Builder reporting is active. Please cancel Credit Builder reporting for this building first.';

    case 'cannot_deactivate_building_with_active_payment_retry':
      return 'This building cannot be deactivated while a payment retry is in progress. Please wait for the payment process to complete and try again.';

    case 'cannot_deactivate_building_with_active_payment_session':
      return 'This building cannot be deactivated while a payment session is active. Please complete or cancel your active payment session first.';

    case 'cannot_deactivate_building_with_pending_transactions':
      return 'This building cannot be deactivated while transactions are pending. Please wait for pending transactions to complete first.';

    case 'cannot_cancel_subscription_if_equifax_subscription_is_active':
      return 'Autopay cannot be disabled while Credit Builder reporting is active. Please cancel Credit Builder reporting for this building first.';

    case 'tenant_mit_subscription_not_found':
    case 'subscription_not_found':
      return 'Autopay setup was not found or is already inactive. Please refresh the page and try again.';

    case 'subscription_already_exists':
    case 'autopay_already_exists':
      return 'Autopay is already enabled for this building. Please check your Autopay settings.';

    case 'social_submission_already_exists':
      return 'You already have an active submission for this. Social Follow can be submitted once per platform, and Social Shout-Out once per platform every 3 months, unless the earlier one was declined.';

    case 'social_submission_not_editable':
      return 'This submission can no longer be edited because it is already under review. Please refresh the page.';

    case 'social_submission_not_found':
      return 'This submission could not be found. Please refresh the page and try again.';

    case 'social_submission_post_url_already_submitted':
      return 'This post URL has already been submitted. Please use a different post.';

    case 'previously_unsubscribed':
      return 'Credit Builder reporting was previously cancelled for this property and cannot be re-enabled. Please contact support if you need assistance.';

    case 'tenant_payment_option_not_active':
      return 'The selected payment method is not active. Please select or add an active payment method.';

    case 'payment_option_not_found':
      return 'Payment method was not found. Please select or add a valid payment method.';

    case 'payment_option_missing_cit_transaction_id':
      return 'Please remove and add the card again, then try setting up Autopay.';

    case 'ip_address_is_required':
      return 'IP address is required. Please check your network connection and try again.';

    case 'tenant_building_not_found_or_inactive':
      return 'Building not found or is currently inactive. Please refresh the page and try again.';

    case 'minimum_rent_amount_should_be_$100':
      return 'Minimum rent amount should be $100.';

    case 'tenant_building_id_required_for_sale_direct_payment_and_add_subscription':
      return 'Building ID is required for processing this transaction.';

    case 'building_status_unchanged':
      return 'The building status is already set to the requested state.';

    case 'tenant_building_not_found':
    case 'building_not_found':
      return 'Building not found. Please refresh the page and try again.';

    case 'pay_number_does_not_belong_to_the_company':
      return 'This TenantPay Account Number does not belong to your property manager. Please verify the account number and try again.';

    case 'resident_inactive':
      return 'This TenantPay Account Number cannot be used because the resident linked to it is inactive. Please contact your property manager.';

    case 'unit_inactive':
      return 'This TenantPay Account Number cannot be used because the unit linked to it is inactive. Please contact your property manager.';

    case 'building_inactive':
      return 'This TenantPay Account Number cannot be used because the building linked to it is inactive. Please contact your property manager.';

    case 'bank_account_inactive':
      return 'This TenantPay Account Number cannot be used because the bank account linked to it is inactive. Please contact your property manager.';

    case 'company_inactive':
      return 'This TenantPay Account Number cannot be used because the property manager company linked to it is inactive. Please contact your property manager.';

    case 'pay_number_not_found':
      return 'TenantPay Account Number not found. Please check the number provided by your landlord.';

    case 'user_email_and_manager_email_cannot_be_same':
      return "Your email address cannot be the same as your property manager's email address. Please enter a different email address.";

    case 'new_password_cannot_be_the_same_as_any_of_your_last_4_passwords':
      return 'Your new password cannot match any of your last 4 passwords. Please choose a different password.';

    case 'invalid_password':
      return 'The password entered is incorrect. Please check your password and try again.';

    case 'missing_parameters':
      return 'Missing required parameters.';

    case 'amount_to_pay_is_zero':
      return 'Amount to pay is zero.';

    case 'building_must_have_pay_number_or_manager':
      return 'Building must have payment number or manager.';

    case 'cannot_delete_yardi_building':
      return 'Properties imported from Yardi cannot be deleted directly. Please contact your property manager to update your lease details.';

    case 'cannot_delete_building_with_units':
      return 'This building cannot be deleted because it has active units. Please remove all units before deleting.';

    case 'cannot_delete_building_with_residents':
      return 'This building cannot be deleted because active residents are assigned to it. Please remove all residents before deleting.';
  }

  // Handle prefix wrappers e.g., "Error updating subscription: ..."
  let cleaned = msg
    .replace(/^Error updating (subscription|payment method|tenant|profile): /i, '')
    .replace(/^Error cancelling (subscription|transaction): /i, '')
    .replace(/^Error getting (building details|tenant details|subscription details): /i, '')
    .replace(/^Error opening order: /i, '');

  if (cleaned !== msg && cleaned.trim()) {
    return formatApiErrorMessage(cleaned.trim(), defaultFallback);
  }

  // Convert any remaining raw snake_case identifier to readable text first
  // (so word boundaries work correctly for term replacement below)
  if (cleaned.includes('_')) {
    const words = cleaned.split('_').filter(Boolean);
    cleaned = words
      .map((w, idx) => (idx === 0 ? w.charAt(0).toUpperCase() + w.slice(1) : w))
      .join(' ');
  }

  // Replace technical terms with "Autopay" (now works correctly on humanized text)
  cleaned = cleaned.replace(/\bmit\s+subscription\b/gi, 'Autopay');
  cleaned = cleaned.replace(/\b(subscription|mit)\b/gi, 'Autopay');

  if (cleaned.includes('_')) {
    return `${cleaned}. Please check your details and try again.`;
  }

  return cleaned;
}

